import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@noor/lib';
import {
  formatDateTime,
  type ModerationEntity,
  type ModerationEntityType,
  type ModerationVersion,
  type Translate,
} from '@/entities/moderation';
import { BranchPricesSection } from './branch-prices-section';
import { ChangedFieldsSection, modifierGroupName } from './changed-fields-section';
import { HeroImage } from './hero-image';
import { DescriptionSection, NameSection } from './localized-sections';
import { ModifiersSection } from './modifiers-section';
import { ModifierChangesBlock } from './patch-value';
import { PhotosSection } from './photos-section';
import { PriceRow } from './price-row';
import { RestaurantCategoriesSection } from './restaurant-categories-section';
import { WeekScheduleSection } from './week-schedule-section';

const HIDDEN_RESTAURANT_FIELDS = new Set([
  'icon',
  'external_id',
  'clear_min_order_amount',
  'background_image',
  'status',
  'working_hours',
  'week_schedule',
  'terminated_reason',
  'termination_reason',
  'terminated_at',
  'timezone',
]);

/**
 * Picture fields (`icon`, `background_image`) stay out of the list while
 * untouched, but a request that changes or clears one must show it — otherwise
 * the moderator sees a change request with nothing to decide on.
 */
function hiddenRestaurantKeys(changed: ReadonlySet<string>): ReadonlySet<string> {
  const keys = new Set(HIDDEN_RESTAURANT_FIELDS);
  for (const key of ['icon', 'background_image']) if (changed.has(key)) keys.delete(key);
  return keys;
}

interface VersionColumnProps {
  /** `live` renders plain; `pending` highlights what differs from live. */
  variant: 'live' | 'pending';
  title: string;
  /** `Обновлено: …` / `Подано: …` line under the title. */
  timestamp?: string | null | undefined;
  timestampLabel?: string | undefined;
  /** Pills pinned to the head (source, "this request"). */
  badges?: ReactNode;
  /** Marks the column of the request the reviewer opened. */
  selected?: boolean;
  entityType: ModerationEntityType;
  own: ModerationEntity | null | undefined;
  /** The live entity a pending column is compared against. */
  other: ModerationEntity | null | undefined;
  /** All top-level fields across the compared versions. */
  compareKeys?: readonly string[] | undefined;
  highlightKeys?: ReadonlySet<string> | undefined;
  /** Keys any compared version changes — the live column marks them as old values. */
  changedUnion?: ReadonlySet<string> | undefined;
  showUnchanged?: boolean | undefined;
  onToggleUnchanged?: (() => void) | undefined;
  /** The slot's version (per-branch rows for products). */
  version?: ModerationVersion | null | undefined;
  liveVersion?: ModerationVersion | null | undefined;
  emptyLabel: string;
  t: Translate;
}

/** One version of the compare grid — hero, price, locales, photos, extras. */
export function VersionColumn({
  variant,
  title,
  timestamp,
  timestampLabel,
  badges,
  selected = false,
  entityType,
  own,
  other,
  compareKeys = [],
  highlightKeys = new Set(),
  changedUnion,
  showUnchanged = true,
  onToggleUnchanged,
  version,
  liveVersion,
  emptyLabel,
  t,
}: VersionColumnProps) {
  const highlight = variant === 'pending';
  const isProduct = entityType === 'products';
  const isRestaurant = entityType === 'restaurants';
  const compared = isProduct || isRestaurant;
  const hasBody = own && Object.keys(own).length > 0;
  // Every column of a compare grid lays out the same slots in the same order.
  // The column is a subgrid spanning the head plus one row per slot, so
  // matching sections of the live and proposed versions share a row — and a
  // section missing from one side leaves a gap instead of shifting the rest.
  const slotCount = isProduct ? 9 : 7;
  const subgrid = compared && hasBody;
  return (
    <section
      className={cn(
        'min-w-0 rounded-[20px] border bg-card p-5',
        subgrid &&
          'flex flex-col gap-6 sm:row-span-(--rows) sm:grid sm:grid-rows-subgrid sm:gap-y-6',
        selected ? 'border-primary/60 ring-1 ring-primary/30' : 'border-border',
      )}
      style={subgrid ? ({ '--rows': String(slotCount + 1) } as CSSProperties) : undefined}
      data-testid={`moderation-version-${variant}`}
      data-selected={selected || undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-lg leading-[normal] font-semibold tracking-[-0.36px] text-foreground">
            {title}
          </h3>
          {timestamp && (
            <p className="mt-1 text-xs font-normal text-muted-foreground tabular-nums">
              {(timestampLabel ?? t('detail.updatedAt', 'Обновлено: {{date}}')).replace(
                '{{date}}',
                formatDateTime(timestamp),
              )}
            </p>
          )}
        </div>
        {badges && (
          <div className="flex shrink-0 flex-wrap justify-end gap-1">{badges}</div>
        )}
      </div>

      {!own || !hasBody ? (
        <p className="mt-6 text-sm text-muted-foreground">{emptyLabel}</p>
      ) : subgrid ? (
        slots()
      ) : (
        <div className="mt-4 flex flex-col gap-6">{slots()}</div>
      )}
    </section>
  );

  function slots(): ReactNode[] {
    if (!own) return [];
    const nodes: ReactNode[] = [
      <div key="hero" className="min-w-0">
        <HeroImage entity={own} placeholder={compared} />
      </div>,
    ];
    if (isProduct) {
      nodes.push(
        <div key="price" className="min-w-0">
          <PriceRow
            own={own}
            other={other}
            highlight={highlight}
            changed={highlightKeys.has('effective_price') || highlightKeys.has('price')}
            t={t}
          />
        </div>,
      );
    }
    nodes.push(
      <div key="name" className="min-w-0">
        <NameSection own={own} other={other} highlight={highlight} t={t} />
      </div>,
    );
    if (isProduct || isRestaurant) {
      nodes.push(
        <div key="description" className="min-w-0">
          <DescriptionSection own={own} other={other} highlight={highlight} t={t} />
        </div>,
      );
    }
    if (isRestaurant) {
      nodes.push(
        <div key="categories" className="min-w-0">
          <RestaurantCategoriesSection
            own={own}
            other={other}
            highlight={highlight}
            t={t}
          />
        </div>,
        <div key="schedule" className="min-w-0">
          <WeekScheduleSection
            schedule={version?.week_schedule ?? own.week_schedule}
            liveSchedule={liveVersion?.week_schedule ?? other?.week_schedule}
            highlight={highlight}
            t={t}
          />
        </div>,
      );
    }
    if (compared) {
      nodes.push(
        <div key="photos" className="min-w-0">
          <PhotosSection entity={own} t={t} placeholder />
        </div>,
      );
    }
    if (isProduct) {
      nodes.push(
        <div key="branches" className="min-w-0">
          <BranchPricesSection
            rows={version?.restaurant_products ?? []}
            liveRows={liveVersion?.restaurant_products ?? []}
            highlight={highlight}
            t={t}
          />
        </div>,
        <div key="modifiers" className="min-w-0">
          <ModifiersSection own={own} other={other} highlight={highlight} t={t} />
        </div>,
        <div key="modifier-changes" className="min-w-0">
          {highlight && (
            <ModifierChangesBlock
              value={(own as Record<string, unknown>).modifier_links}
              groupName={(id) => modifierGroupName([own, ...(other ? [other] : [])], id)}
              t={t}
            />
          )}
        </div>,
      );
    }
    nodes.push(
      <div key="fields" className="min-w-0">
        <ChangedFieldsSection
          own={own}
          keys={compareKeys}
          highlightKeys={
            variant === 'live' ? (changedUnion ?? highlightKeys) : highlightKeys
          }
          tone={variant === 'live' ? 'old' : 'new'}
          changedUnion={changedUnion}
          showUnchanged={showUnchanged}
          {...(onToggleUnchanged ? { onToggleUnchanged } : {})}
          hiddenKeys={
            isRestaurant ? hiddenRestaurantKeys(highlightKeys) : new Set<string>()
          }
          title={t('detail.allFields', 'Все поля')}
          peers={[other]}
          t={t}
        />
      </div>,
    );
    return nodes;
  }
}
